import { useState, useRef, useEffect } from 'react';
import * as XLSX from 'xlsx';
import { adminAtas, adminCategorias } from '@/mocks/admin';
import AdminHeader from '../components/Header';
import { uploadToCloudflareR2 } from '@/services/cloudflareUpload';
import { deleteAta, fetchAtaCategorias, fetchAtas, saveAta } from '@/services/catalog';

interface Categoria {
  id: number;
  nome: string;
}

interface Ata {
  id: number;
  numero: string;
  nome: string;
  orgao: string;
  uasg: string;
  estado: string;
  esfera: string;
  objeto: string;
  dataInicio: string;
  dataFim: string;
  dataEdital: string;
  status: string;
  visibilidade: string;
  categorias: number[];
  anexos: string[];
  pesquisas: number;
}

const statusOptions = ['Ativa', 'Encerrada'];
const visibilidadeOptions = ['Visível', 'Oculta'];
const esferaOptions = ['Federal', 'Estadual', 'Municipal'];

const estadosBrasil = [
  'AC', 'AL', 'AP', 'AM', 'BA', 'CE', 'DF', 'ES', 'GO',
  'MA', 'MT', 'MS', 'MG', 'PA', 'PB', 'PR', 'PE', 'PI',
  'RJ', 'RN', 'RS', 'RO', 'RR', 'SC', 'SP', 'SE', 'TO',
];

export default function AtasPage() {
  const [atas, setAtas] = useState<Ata[]>(adminAtas);
  const [categorias, setCategorias] = useState<Categoria[]>(adminCategorias);
  const [showModal, setShowModal] = useState(false);
  const [showCategoriaModal, setShowCategoriaModal] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [categoriaSearch, setCategoriaSearch] = useState('');
  const [categoriaDropdownOpen, setCategoriaDropdownOpen] = useState(false);
  const [novaCategoriaNome, setNovaCategoriaNome] = useState('');
  const [uploadingAnexos, setUploadingAnexos] = useState(false);
  const [anexoUploadError, setAnexoUploadError] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);

  const [formData, setFormData] = useState({
    numero: '',
    nome: '',
    orgao: '',
    uasg: '',
    estado: '',
    esfera: '',
    objeto: '',
    dataInicio: '',
    dataFim: '',
    dataEdital: '',
    status: 'Ativa',
    visibilidade: 'Visível',
    categorias: [] as number[],
    anexos: [] as string[],
  });

  // Close category dropdown on outside click
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setCategoriaDropdownOpen(false);
        setCategoriaSearch('');
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    Promise.all([fetchAtas(), fetchAtaCategorias()]).then(([atasData, categoriasData]) => {
      setAtas(atasData);
      setCategorias(categoriasData);
    });
  }, []);

  const filteredAtas = atas.filter(
    (ata) =>
      ata.numero.toLowerCase().includes(searchTerm.toLowerCase()) ||
      ata.nome.toLowerCase().includes(searchTerm.toLowerCase()) ||
      ata.orgao.toLowerCase().includes(searchTerm.toLowerCase()) ||
      ata.uasg.toLowerCase().includes(searchTerm.toLowerCase()) ||
      ata.objeto.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredCategorias = categorias.filter((cat) =>
    cat.nome.toLowerCase().includes(categoriaSearch.toLowerCase())
  );

  const getCategoriaNomes = (ids: number[]) =>
    ids.map((id) => categorias.find((c) => c.id === id)?.nome || '').filter(Boolean).join(', ');

  const resetForm = () => {
    setFormData({
      numero: '',
      nome: '',
      orgao: '',
      uasg: '',
      estado: '',
      esfera: '',
      objeto: '',
      dataInicio: '',
      dataFim: '',
      dataEdital: '',
      status: 'Ativa',
      visibilidade: 'Visível',
      categorias: [],
      anexos: [],
    });
  };

  const openNewModal = () => {
    setEditingId(null);
    resetForm();
    setShowModal(true);
  };

  const openEditModal = (ata: Ata) => {
    setEditingId(ata.id);
    setFormData({
      numero: ata.numero,
      nome: ata.nome,
      orgao: ata.orgao,
      uasg: ata.uasg,
      estado: ata.estado || '',
      esfera: ata.esfera || '',
      objeto: ata.objeto,
      dataInicio: ata.dataInicio,
      dataFim: ata.dataFim,
      dataEdital: ata.dataEdital,
      status: ata.status,
      visibilidade: ata.visibilidade,
      categorias: [...ata.categorias],
      anexos: [...ata.anexos],
    });
    setShowModal(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (editingId) {
      try {
        await saveAta({ id: editingId, ...formData, pesquisas: atas.find((ata) => ata.id === editingId)?.pesquisas || 0 });
      } catch (error) {
        console.error('Falha ao salvar ata no Supabase.', error);
        window.alert('Nao foi possivel salvar a ATA no Supabase. Verifique a conexao e tente novamente.');
        return;
      }
      setAtas((prev) =>
        prev.map((ata) =>
          ata.id === editingId ? { ...ata, ...formData, pesquisas: ata.pesquisas } : ata
        )
      );
    } else {
      const newAta: Ata = {
        id: Math.max(...atas.map((a) => a.id), 0) + 1,
        ...formData,
        pesquisas: 0,
      };
      try {
        const savedAta = await saveAta(newAta);
        setAtas((prev) => [...prev, savedAta || newAta]);
      } catch (error) {
        console.error('Falha ao salvar ata no Supabase.', error);
        window.alert('Nao foi possivel salvar a ATA no Supabase. Verifique a conexao e tente novamente.');
        return;
      }
    }
    setShowModal(false);
  };

  const handleDelete = (id: number) => {
    if (confirm('Tem certeza que deseja excluir esta ata?')) {
      deleteAta(id).catch((error) => console.warn('Falha ao excluir ata no Supabase.', error));
      setAtas((prev) => prev.filter((ata) => ata.id !== id));
    }
  };

  const handleExportExcel = () => {
    const dataToExport = filteredAtas.length > 0 ? filteredAtas : atas;
    const rows = dataToExport.map((ata) => ({
      'Número': ata.numero,
      'Nome': ata.nome,
      'Órgão': ata.orgao,
      'UASG': ata.uasg,
      'Estado (UF)': ata.estado || '',
      'Esfera': ata.esfera || '',
      'Objeto': ata.objeto,
      'Data do Edital': ata.dataEdital ? new Date(ata.dataEdital + 'T00:00:00').toLocaleDateString('pt-BR') : '',
      'Data Início': ata.dataInicio ? new Date(ata.dataInicio + 'T00:00:00').toLocaleDateString('pt-BR') : '',
      'Data Fim': ata.dataFim ? new Date(ata.dataFim + 'T00:00:00').toLocaleDateString('pt-BR') : '',
      'Status': ata.status,
      'Visibilidade': ata.visibilidade,
      'Categorias': getCategoriaNomes(ata.categorias),
      'Anexos': ata.anexos.join('; '),
      'Pesquisas': ata.pesquisas,
    }));

    const ws = XLSX.utils.json_to_sheet(rows);
    const colWidths = [
      { wch: 16 }, { wch: 40 }, { wch: 32 }, { wch: 10 },
      { wch: 60 }, { wch: 14 }, { wch: 14 }, { wch: 14 },
      { wch: 12 }, { wch: 14 }, { wch: 30 }, { wch: 30 }, { wch: 12 },
    ];
    ws['!cols'] = colWidths;

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Atas');

    const today = new Date().toISOString().slice(0, 10);
    XLSX.writeFile(wb, `atas-donnos-${today}.xlsx`);
  };

  const handleFileAdd = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files) {
      const ownerName = formData.numero.trim() || formData.nome.trim() || 'ata-sem-nome';
      const newFiles: string[] = [];
      setUploadingAnexos(true);
      setAnexoUploadError('');

      try {
        for (const file of Array.from(files)) {
          const uploaded = await uploadToCloudflareR2(file, 'ata-anexo', ownerName);
          newFiles.push(`${uploaded.name}::${uploaded.downloadUrl}`);
        }

        setFormData((prev) => ({
          ...prev,
          anexos: [...prev.anexos, ...newFiles].filter((f, i, arr) => arr.indexOf(f) === i),
        }));
      } catch (error) {
        console.error('Falha no upload de anexo para o Cloudflare R2.', error);
        setAnexoUploadError(
          error instanceof Error
            ? `Nao foi possivel enviar para o Cloudflare R2: ${error.message}`
            : 'Nao foi possivel enviar para o Cloudflare R2. Verifique a configuracao do bucket.',
        );
      } finally {
        setUploadingAnexos(false);
      }
    }
    e.target.value = '';
  };

  const handleRemoveFile = (fileName: string) => {
    setFormData((prev) => ({
      ...prev,
      anexos: prev.anexos.filter((f) => f !== fileName),
    }));
  };

  const toggleCategoria = (catId: number) => {
    setFormData((prev) => ({
      ...prev,
      categorias: prev.categorias.includes(catId)
        ? prev.categorias.filter((id) => id !== catId)
        : [...prev.categorias, catId],
    }));
  };

  const handleCriarCategoria = () => {
    if (!novaCategoriaNome.trim()) return;
    const newId = Math.max(...categorias.map((c) => c.id), 0) + 1;
    const newCat: Categoria = { id: newId, nome: novaCategoriaNome.trim() };
    setCategorias((prev) => [...prev, newCat]);
    setFormData((prev) => ({
      ...prev,
      categorias: [...prev.categorias, newId],
    }));
    setNovaCategoriaNome('');
    setShowCategoriaModal(false);
  };

  const handleDeleteCategoria = (catId: number) => {
    if (confirm('Excluir esta categoria? Ela será removida de todas as atas vinculadas.')) {
      setCategorias((prev) => prev.filter((c) => c.id !== catId));
      setAtas((prev) =>
        prev.map((ata) => ({
          ...ata,
          categorias: ata.categorias.filter((id) => id !== catId),
        }))
      );
      setFormData((prev) => ({
        ...prev,
        categorias: prev.categorias.filter((id) => id !== catId),
      }));
    }
  };

  return (
    <div>
      <AdminHeader title="Gerenciamento de Atas" subtitle="Cadastre e gerencie as atas de registro de preços" />

      <div className="p-8">
        {/* Toolbar */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
          <div className="relative w-full sm:w-80">
            <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm"></i>
            <input
              type="text"
              placeholder="Buscar por número, nome, órgão, UASG..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 rounded-lg border border-gray-200 text-sm focus:outline-none focus:border-donnos-green focus:ring-1 focus:ring-donnos-green"
            />
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={handleExportExcel}
              className="inline-flex items-center gap-2 bg-white hover:bg-gray-50 text-gray-700 font-medium px-4 py-2.5 rounded-lg border border-gray-200 transition-colors whitespace-nowrap text-sm"
            >
              <i className="ri-file-excel-2-line text-donnos-green"></i>
              Exportar Excel
            </button>
            <button
              onClick={() => {
                setShowCategoriaModal(true);
                setNovaCategoriaNome('');
              }}
              className="inline-flex items-center gap-2 bg-white hover:bg-gray-50 text-gray-700 font-medium px-4 py-2.5 rounded-lg border border-gray-200 transition-colors whitespace-nowrap text-sm"
            >
              <i className="ri-price-tag-3-line"></i>
              Gerenciar Categorias
            </button>
            <button
              onClick={openNewModal}
              className="inline-flex items-center gap-2 bg-donnos-green hover:bg-donnos-green-dark text-white font-semibold px-5 py-2.5 rounded-lg transition-colors whitespace-nowrap text-sm"
            >
              <i className="ri-add-line"></i>
              Nova Ata
            </button>
          </div>
        </div>

        {/* Table */}
        <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-gray-50">
                  <th className="text-left text-xs font-semibold text-gray-500 uppercase px-5 py-3 whitespace-nowrap">Número</th>
                  <th className="text-left text-xs font-semibold text-gray-500 uppercase px-5 py-3 whitespace-nowrap">Nome</th>
                  <th className="text-left text-xs font-semibold text-gray-500 uppercase px-5 py-3 whitespace-nowrap">Órgão</th>
                  <th className="text-left text-xs font-semibold text-gray-500 uppercase px-5 py-3 whitespace-nowrap">UASG</th>
                  <th className="text-left text-xs font-semibold text-gray-500 uppercase px-5 py-3 whitespace-nowrap">Estado</th>
                  <th className="text-left text-xs font-semibold text-gray-500 uppercase px-5 py-3 whitespace-nowrap">Esfera</th>
                  <th className="text-left text-xs font-semibold text-gray-500 uppercase px-5 py-3 whitespace-nowrap">Objeto</th>
                  <th className="text-left text-xs font-semibold text-gray-500 uppercase px-5 py-3 whitespace-nowrap">Data Edital</th>
                  <th className="text-left text-xs font-semibold text-gray-500 uppercase px-5 py-3 whitespace-nowrap">Status</th>
                  <th className="text-left text-xs font-semibold text-gray-500 uppercase px-5 py-3 whitespace-nowrap">Visibilidade</th>
                  <th className="text-left text-xs font-semibold text-gray-500 uppercase px-5 py-3 whitespace-nowrap">Categorias</th>
                  <th className="text-left text-xs font-semibold text-gray-500 uppercase px-5 py-3 whitespace-nowrap">Pesquisas</th>
                  <th className="text-left text-xs font-semibold text-gray-500 uppercase px-5 py-3 whitespace-nowrap">Ações</th>
                </tr>
              </thead>
              <tbody>
                {filteredAtas.map((ata) => (
                  <tr key={ata.id} className="border-t border-gray-50 hover:bg-gray-50/50 transition-colors">
                    <td className="px-5 py-3">
                      <span className="text-sm font-semibold text-gray-900 whitespace-nowrap">{ata.numero}</span>
                    </td>
                    <td className="px-5 py-3">
                      <span className="text-sm text-gray-700 whitespace-nowrap max-w-[180px] truncate block">{ata.nome}</span>
                    </td>
                    <td className="px-5 py-3">
                      <span className="text-sm text-gray-600 whitespace-nowrap">{ata.orgao}</span>
                    </td>
                    <td className="px-5 py-3">
                      <span className="text-sm text-gray-600 font-mono whitespace-nowrap">{ata.uasg}</span>
                    </td>
                    <td className="px-5 py-3">
                      <span className="text-sm text-gray-600 font-medium whitespace-nowrap">{ata.estado || '—'}</span>
                    </td>
                    <td className="px-5 py-3">
                      <span className={`inline-flex px-2 py-1 rounded-full text-xs font-medium whitespace-nowrap ${
                        ata.esfera === 'Federal' ? 'bg-blue-50 text-blue-600' :
                        ata.esfera === 'Estadual' ? 'bg-green-50 text-green-600' :
                        ata.esfera === 'Municipal' ? 'bg-orange-50 text-orange-600' :
                        'bg-gray-100 text-gray-500'
                      }`}>
                        {ata.esfera || '—'}
                      </span>
                    </td>
                    <td className="px-5 py-3">
                      <span className="text-sm text-gray-600 max-w-[220px] truncate block">{ata.objeto}</span>
                    </td>
                    <td className="px-5 py-3">
                      <span className="text-sm text-gray-600 whitespace-nowrap">
                        {new Date(ata.dataEdital + 'T00:00:00').toLocaleDateString('pt-BR')}
                      </span>
                    </td>
                    <td className="px-5 py-3">
                      <span
                        className={`inline-flex px-2 py-1 rounded-full text-xs font-medium whitespace-nowrap ${
                          ata.status === 'Ativa'
                            ? 'bg-donnos-green/10 text-donnos-green'
                            : 'bg-gray-100 text-gray-500'
                        }`}
                      >
                        {ata.status}
                      </span>
                    </td>
                    <td className="px-5 py-3">
                      <span
                        className={`inline-flex px-2 py-1 rounded-full text-xs font-medium whitespace-nowrap ${
                          ata.visibilidade === 'Visível'
                            ? 'bg-blue-50 text-blue-600'
                            : 'bg-amber-50 text-amber-600'
                        }`}
                      >
                        <i className={`${ata.visibilidade === 'Visível' ? 'ri-eye-line' : 'ri-eye-off-line'} mr-1`}></i>
                        {ata.visibilidade}
                      </span>
                    </td>
                    <td className="px-5 py-3">
                      <div className="flex flex-wrap gap-1">
                        {ata.categorias.length > 0 ? (
                          ata.categorias.slice(0, 2).map((catId) => {
                            const cat = categorias.find((c) => c.id === catId);
                            return cat ? (
                              <span key={catId} className="inline-flex px-1.5 py-0.5 rounded-full bg-gray-100 text-gray-600 text-xs whitespace-nowrap">
                                {cat.nome}
                              </span>
                            ) : null;
                          })
                        ) : (
                          <span className="text-xs text-gray-400">—</span>
                        )}
                        {ata.categorias.length > 2 && (
                          <span className="inline-flex px-1.5 py-0.5 rounded-full bg-gray-100 text-gray-500 text-xs">
                            +{ata.categorias.length - 2}
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-5 py-3">
                      <span className="text-sm text-gray-900 font-medium">{ata.pesquisas}</span>
                    </td>
                    <td className="px-5 py-3">
                      <div className="flex items-center gap-1.5">
                        <button
                          onClick={() => openEditModal(ata)}
                          className="w-8 h-8 rounded-lg bg-gray-100 hover:bg-gray-200 flex items-center justify-center transition-colors"
                          title="Editar"
                        >
                          <i className="ri-edit-line text-gray-600 text-sm"></i>
                        </button>
                        <button
                          onClick={() => handleDelete(ata.id)}
                          className="w-8 h-8 rounded-lg bg-red-50 hover:bg-red-100 flex items-center justify-center transition-colors"
                          title="Excluir"
                        >
                          <i className="ri-delete-bin-line text-red-500 text-sm"></i>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {filteredAtas.length === 0 && (
            <div className="text-center py-12">
              <i className="ri-file-list-3-line text-4xl text-gray-300 block mb-3"></i>
              <p className="text-gray-500">Nenhuma ata encontrada</p>
            </div>
          )}
        </div>

        {/* Pagination info */}
        <div className="mt-4 text-sm text-gray-500">
          Exibindo {filteredAtas.length} de {atas.length} atas
        </div>
      </div>

      {/* Modal de Cadastro/Edição de ATA */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-start justify-center z-50 p-4 pt-10 pb-10">
          <div className="bg-white rounded-xl w-full max-w-2xl max-h-[calc(100vh-80px)] overflow-y-auto">
            <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between sticky top-0 bg-white z-10 rounded-t-xl">
              <h3 className="text-lg font-bold text-gray-900">
                {editingId ? 'Editar Ata' : 'Nova Ata'}
              </h3>
              <button
                onClick={() => setShowModal(false)}
                className="w-8 h-8 rounded-lg hover:bg-gray-100 flex items-center justify-center transition-colors"
              >
                <i className="ri-close-line text-gray-500"></i>
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-5">
              {/* Número + Nome */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Número da ATA <span className="text-red-400">*</span>
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.numero}
                    onChange={(e) => setFormData({ ...formData, numero: e.target.value })}
                    placeholder="Ex: ATA-2024-001"
                    className="w-full px-4 py-2.5 rounded-lg border border-gray-200 text-sm focus:outline-none focus:border-donnos-green focus:ring-1 focus:ring-donnos-green"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Nome da ATA <span className="text-red-400">*</span>
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.nome}
                    onChange={(e) => setFormData({ ...formData, nome: e.target.value })}
                    placeholder="Ex: Aquisição de Equipamentos de TI"
                    className="w-full px-4 py-2.5 rounded-lg border border-gray-200 text-sm focus:outline-none focus:border-donnos-green focus:ring-1 focus:ring-donnos-green"
                  />
                </div>
              </div>

              {/* Órgão + UASG */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Órgão <span className="text-red-400">*</span>
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.orgao}
                    onChange={(e) => setFormData({ ...formData, orgao: e.target.value })}
                    placeholder="Ex: Ministério da Educação"
                    className="w-full px-4 py-2.5 rounded-lg border border-gray-200 text-sm focus:outline-none focus:border-donnos-green focus:ring-1 focus:ring-donnos-green"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    UASG <span className="text-red-400">*</span>
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.uasg}
                    onChange={(e) => setFormData({ ...formData, uasg: e.target.value })}
                    placeholder="Ex: 150001"
                    className="w-full px-4 py-2.5 rounded-lg border border-gray-200 text-sm focus:outline-none focus:border-donnos-green focus:ring-1 focus:ring-donnos-green"
                  />
                </div>
              </div>

              {/* Estado + Esfera */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Estado (UF) <span className="text-red-400">*</span>
                  </label>
                  <select
                    required
                    value={formData.estado}
                    onChange={(e) => setFormData({ ...formData, estado: e.target.value })}
                    className="w-full px-4 py-2.5 rounded-lg border border-gray-200 text-sm focus:outline-none focus:border-donnos-green focus:ring-1 focus:ring-donnos-green bg-white cursor-pointer"
                  >
                    <option value="">Selecione o estado...</option>
                    {estadosBrasil.map((uf) => (
                      <option key={uf} value={uf}>{uf}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Esfera <span className="text-red-400">*</span>
                  </label>
                  <select
                    required
                    value={formData.esfera}
                    onChange={(e) => setFormData({ ...formData, esfera: e.target.value })}
                    className="w-full px-4 py-2.5 rounded-lg border border-gray-200 text-sm focus:outline-none focus:border-donnos-green focus:ring-1 focus:ring-donnos-green bg-white cursor-pointer"
                  >
                    <option value="">Selecione a esfera...</option>
                    {esferaOptions.map((e) => (
                      <option key={e} value={e}>{e}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Objeto */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Objeto <span className="text-red-400">*</span>
                </label>
                <textarea
                  required
                  value={formData.objeto}
                  onChange={(e) => setFormData({ ...formData, objeto: e.target.value })}
                  placeholder="Descreva o objeto da ata de forma detalhada"
                  rows={3}
                  maxLength={500}
                  className="w-full px-4 py-2.5 rounded-lg border border-gray-200 text-sm focus:outline-none focus:border-donnos-green focus:ring-1 focus:ring-donnos-green resize-none"
                />
                <p className="text-xs text-gray-400 mt-1 text-right">{formData.objeto.length}/500</p>
              </div>

              {/* Datas */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Data do Edital <span className="text-red-400">*</span>
                  </label>
                  <input
                    type="date"
                    required
                    value={formData.dataEdital}
                    onChange={(e) => setFormData({ ...formData, dataEdital: e.target.value })}
                    className="w-full px-4 py-2.5 rounded-lg border border-gray-200 text-sm focus:outline-none focus:border-donnos-green focus:ring-1 focus:ring-donnos-green"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Data Início
                  </label>
                  <input
                    type="date"
                    value={formData.dataInicio}
                    onChange={(e) => setFormData({ ...formData, dataInicio: e.target.value })}
                    className="w-full px-4 py-2.5 rounded-lg border border-gray-200 text-sm focus:outline-none focus:border-donnos-green focus:ring-1 focus:ring-donnos-green"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Data Fim
                  </label>
                  <input
                    type="date"
                    value={formData.dataFim}
                    onChange={(e) => setFormData({ ...formData, dataFim: e.target.value })}
                    className="w-full px-4 py-2.5 rounded-lg border border-gray-200 text-sm focus:outline-none focus:border-donnos-green focus:ring-1 focus:ring-donnos-green"
                  />
                </div>
              </div>

              {/* Status + Visibilidade */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                  <select
                    value={formData.status}
                    onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                    className="w-full px-4 py-2.5 rounded-lg border border-gray-200 text-sm focus:outline-none focus:border-donnos-green focus:ring-1 focus:ring-donnos-green bg-white"
                  >
                    {statusOptions.map((s) => (
                      <option key={s} value={s}>{s}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Visibilidade</label>
                  <select
                    value={formData.visibilidade}
                    onChange={(e) => setFormData({ ...formData, visibilidade: e.target.value })}
                    className="w-full px-4 py-2.5 rounded-lg border border-gray-200 text-sm focus:outline-none focus:border-donnos-green focus:ring-1 focus:ring-donnos-green bg-white"
                  >
                    {visibilidadeOptions.map((v) => (
                      <option key={v} value={v}>{v}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Categorias - Multi-select com busca */}
              <div ref={dropdownRef}>
                <label className="block text-sm font-medium text-gray-700 mb-1">Categorias</label>
                <div className="relative">
                  <button
                    type="button"
                    onClick={() => {
                      setCategoriaDropdownOpen(!categoriaDropdownOpen);
                      setCategoriaSearch('');
                    }}
                    className="w-full px-4 py-2.5 rounded-lg border border-gray-200 text-sm text-left focus:outline-none focus:border-donnos-green focus:ring-1 focus:ring-donnos-green bg-white flex items-center justify-between"
                  >
                    <span className={formData.categorias.length === 0 ? 'text-gray-400' : 'text-gray-900'}>
                      {formData.categorias.length === 0
                        ? 'Selecione uma ou mais categorias...'
                        : `${formData.categorias.length} categoria(s) selecionada(s)`}
                    </span>
                    <i className={`ri-arrow-down-s-line text-gray-400 transition-transform ${categoriaDropdownOpen ? 'rotate-180' : ''}`}></i>
                  </button>

                  {categoriaDropdownOpen && (
                    <div className="absolute z-20 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-64 overflow-hidden flex flex-col">
                      {/* Search */}
                      <div className="p-2 border-b border-gray-100">
                        <div className="relative">
                          <i className="ri-search-line absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400 text-xs"></i>
                          <input
                            type="text"
                            placeholder="Buscar categoria..."
                            value={categoriaSearch}
                            onChange={(e) => setCategoriaSearch(e.target.value)}
                            className="w-full pl-8 pr-3 py-2 rounded-md border border-gray-200 text-xs focus:outline-none focus:border-donnos-green"
                            onClick={(e) => e.stopPropagation()}
                          />
                        </div>
                      </div>

                      {/* Category list */}
                      <div className="overflow-y-auto max-h-40">
                        {filteredCategorias.length === 0 && categoriaSearch && (
                          <div className="px-3 py-4 text-center">
                            <p className="text-xs text-gray-400 mb-2">Nenhuma categoria encontrada</p>
                            <button
                              type="button"
                              onClick={() => {
                                setNovaCategoriaNome(categoriaSearch);
                                setCategoriaDropdownOpen(false);
                                setShowCategoriaModal(true);
                              }}
                              className="text-xs text-donnos-green hover:text-donnos-green-dark font-medium"
                            >
                              + Criar &quot;{categoriaSearch}&quot;
                            </button>
                          </div>
                        )}
                        {filteredCategorias.length === 0 && !categoriaSearch && (
                          <p className="px-3 py-4 text-xs text-gray-400 text-center">Nenhuma categoria cadastrada</p>
                        )}
                        {filteredCategorias.map((cat) => {
                          const selected = formData.categorias.includes(cat.id);
                          return (
                            <label
                              key={cat.id}
                              className="flex items-center gap-2.5 px-3 py-2 hover:bg-gray-50 cursor-pointer transition-colors"
                            >
                              <input
                                type="checkbox"
                                checked={selected}
                                onChange={() => toggleCategoria(cat.id)}
                                className="w-4 h-4 rounded border-gray-300 text-donnos-green focus:ring-donnos-green"
                              />
                              <span className="text-sm text-gray-700">{cat.nome}</span>
                            </label>
                          );
                        })}
                      </div>

                      {/* Footer */}
                      <div className="p-2 border-t border-gray-100">
                        <button
                          type="button"
                          onClick={() => {
                            setCategoriaDropdownOpen(false);
                            setShowCategoriaModal(true);
                            setNovaCategoriaNome('');
                          }}
                          className="w-full text-center text-xs text-donnos-green hover:text-donnos-green-dark font-medium py-1.5 rounded-md hover:bg-donnos-green/5 transition-colors"
                        >
                          <i className="ri-add-line mr-1"></i>
                          Criar nova categoria
                        </button>
                      </div>
                    </div>
                  )}
                </div>

                {/* Selected categories chips */}
                {formData.categorias.length > 0 && (
                  <div className="flex flex-wrap gap-1.5 mt-2">
                    {formData.categorias.map((catId) => {
                      const cat = categorias.find((c) => c.id === catId);
                      return cat ? (
                        <span
                          key={catId}
                          className="inline-flex items-center gap-1 pl-2.5 pr-1.5 py-1 rounded-full bg-donnos-green/10 text-donnos-green text-xs font-medium"
                        >
                          {cat.nome}
                          <button
                            type="button"
                            onClick={() => toggleCategoria(catId)}
                            className="w-4 h-4 rounded-full hover:bg-donnos-green/20 flex items-center justify-center"
                          >
                            <i className="ri-close-line text-[10px]"></i>
                          </button>
                        </span>
                      ) : null;
                    })}
                  </div>
                )}
              </div>

              {/* Anexos */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Anexos</label>
                <div className="border-2 border-dashed border-gray-200 rounded-lg p-4 text-center hover:border-donnos-green/40 transition-colors">
                  <input
                    type="file"
                    id="ata-anexos"
                    multiple
                    onChange={handleFileAdd}
                    className="hidden"
                    accept=".pdf,.doc,.docx,.xls,.xlsx,.zip,.rar,.png,.jpg,.jpeg"
                    disabled={uploadingAnexos}
                  />
                  <label htmlFor="ata-anexos" className={uploadingAnexos ? 'cursor-wait opacity-70' : 'cursor-pointer'}>
                    <i className="ri-upload-cloud-2-line text-2xl text-gray-300 block mb-1"></i>
                    <p className="text-sm text-gray-500">
                      <span className="text-donnos-green font-medium">
                        {uploadingAnexos ? 'Enviando para o Cloudflare R2...' : 'Clique para anexar'}
                      </span>{' '}
                      ou arraste arquivos
                    </p>
                    <p className="text-xs text-gray-400 mt-0.5">PDF, DOC, XLS, ZIP, imagens (até 10MB cada)</p>
                  </label>
                </div>
                {anexoUploadError && (
                  <div className="mt-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                    {anexoUploadError}
                  </div>
                )}

                {/* File list */}
                {formData.anexos.length > 0 && (
                  <div className="mt-3 space-y-1.5">
                    {formData.anexos.map((file) => (
                      <div
                        key={file}
                        className="flex items-center justify-between px-3 py-2 bg-gray-50 rounded-lg text-sm"
                      >
                        <div className="flex items-center gap-2">
                          <i className="ri-file-line text-gray-400"></i>
                          <span className="text-gray-700 truncate max-w-[400px]">{file.split('::')[0]}</span>
                        </div>
                        <button
                          type="button"
                          onClick={() => handleRemoveFile(file)}
                          className="w-6 h-6 rounded hover:bg-red-50 flex items-center justify-center transition-colors"
                        >
                          <i className="ri-close-line text-red-400 text-sm"></i>
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Actions */}
              <div className="flex items-center gap-3 pt-2 border-t border-gray-100">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="flex-1 px-4 py-2.5 rounded-lg border border-gray-200 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-2.5 rounded-lg bg-donnos-green hover:bg-donnos-green-dark text-white text-sm font-medium transition-colors"
                >
                  {editingId ? 'Salvar alterações' : 'Cadastrar ata'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal de Criar/Gerenciar Categorias */}
      {showCategoriaModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] p-4">
          <div className="bg-white rounded-xl w-full max-w-md">
            <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
              <h3 className="text-lg font-bold text-gray-900">
                {novaCategoriaNome ? 'Criar Categoria' : 'Gerenciar Categorias'}
              </h3>
              <button
                onClick={() => {
                  setShowCategoriaModal(false);
                  setNovaCategoriaNome('');
                }}
                className="w-8 h-8 rounded-lg hover:bg-gray-100 flex items-center justify-center transition-colors"
              >
                <i className="ri-close-line text-gray-500"></i>
              </button>
            </div>

            <div className="p-6 space-y-4">
              {/* Nova categoria input */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Nova Categoria</label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={novaCategoriaNome}
                    onChange={(e) => setNovaCategoriaNome(e.target.value)}
                    placeholder="Nome da categoria"
                    className="flex-1 px-4 py-2.5 rounded-lg border border-gray-200 text-sm focus:outline-none focus:border-donnos-green focus:ring-1 focus:ring-donnos-green"
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        handleCriarCategoria();
                      }
                    }}
                  />
                  <button
                    type="button"
                    onClick={handleCriarCategoria}
                    disabled={!novaCategoriaNome.trim()}
                    className="px-4 py-2.5 rounded-lg bg-donnos-green hover:bg-donnos-green-dark text-white text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                  >
                    <i className="ri-add-line mr-1"></i>
                    Criar
                  </button>
                </div>
              </div>

              {/* Lista de categorias existentes */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Categorias existentes ({categorias.length})
                </label>
                <div className="max-h-60 overflow-y-auto space-y-1">
                  {categorias.length === 0 ? (
                    <p className="text-sm text-gray-400 text-center py-4">Nenhuma categoria cadastrada</p>
                  ) : (
                    categorias.map((cat) => (
                      <div
                        key={cat.id}
                        className="flex items-center justify-between px-3 py-2 bg-gray-50 rounded-lg"
                      >
                        <span className="text-sm text-gray-700">{cat.nome}</span>
                        <button
                          type="button"
                          onClick={() => handleDeleteCategoria(cat.id)}
                          className="w-7 h-7 rounded hover:bg-red-50 flex items-center justify-center transition-colors"
                          title="Excluir categoria"
                        >
                          <i className="ri-delete-bin-line text-red-400 text-sm"></i>
                        </button>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>

            <div className="px-6 py-3 border-t border-gray-100 flex justify-end">
              <button
                type="button"
                onClick={() => {
                  setShowCategoriaModal(false);
                  setNovaCategoriaNome('');
                }}
                className="px-4 py-2 rounded-lg bg-gray-100 hover:bg-gray-200 text-sm font-medium text-gray-700 transition-colors"
              >
                Fechar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
